import React, { useContext, useEffect, useMemo, useState } from 'react'
import axios from '../../../../config/axios'
import { Eye, Pencil, Power, Copy, ArrowLeftRight } from 'lucide-react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import { useTenant } from '../../../../contexts/TenantContext'
import ApiService from '../../../../services/ApiService'
import ModalContext from '../../../../contexts/ModalContext'
import StockContext from '../../../../contexts/StockContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildResourceColumns, resourceScreenKey, Resource } from './resourceColumns'

interface ContextType {
  search: string;
  appliedFilters?: Filter[];
  refresh?: boolean;
}

const TAKE = 50

const ResourcesTable: React.FC = () => {
  const { isFashionTenant } = useTenant()
  const [resources, setResources] = useState<Resource[]>([])
  const [total, setTotal] = useState<number>(0)
  const [skip, setSkip] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [localRefresh, setLocalRefresh] = useState(0)
  const [statusTarget, setStatusTarget] = useState<Resource | null>(null)
  const { setModalActive, setModalSection, setItIsUpdate } = useContext(ModalContext)
  const { setStock } = useContext(StockContext)
  const { setFeedback } = useContext(FeedbackContext)
  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'

  const columns = useMemo(() => buildResourceColumns(isFashionTenant), [isFashionTenant])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    resourceScreenKey(isFashionTenant),
    columns,
  )

  const navigate = useNavigate()

  const context = useOutletContext<ContextType>() || { search: '', appliedFilters: [], refresh: false };
  const { search, appliedFilters = [], refresh } = context;

  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    const getResources = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
          params: {
            search: debouncedSearch,
            skip: 0,
            take: TAKE,
            filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
          }
        })
        if (response.data) {
          const items = response.data.data ?? response.data
          const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

          setResources(items)
          setTotal(totalItems)
          setSkip(TAKE)
        }
      } catch (error) {
        console.error("Erro ao buscar recursos:", error);
      }
    }
    getResources()
  }, [debouncedSearch, appliedFilters, localRefresh, refresh])

  const handleConfirmStatusChange = async () => {
    if (!statusTarget) return
    const newStatus = statusTarget.isActivated === false
    try {
      await ApiService.resources.update(Number(statusTarget.id), { isActivated: newStatus })
      setFeedback({
        message: `${itemName} ${newStatus ? 'ativado' : 'inativado'} com sucesso!`,
        isOpen: true,
        statusOk: true,
      })
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || `Erro ao ${newStatus ? 'ativar' : 'inativar'} ${itemName.toLowerCase()}`
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    } finally {
      setStatusTarget(null)
    }
  }

  const handleNewMovement = (resource: Resource) => {
    setStock({
      codeId: resource.codeId,
      description: resource.name,
      totalWeight: resource.totalWeight != null ? String(resource.totalWeight) : '0',
      lengthInMm: resource.lengthInMm != null ? String(resource.lengthInMm) : '0',
      isResource: true,
      id: resource.id,
    } as any)
    setItIsUpdate(true)
    setModalSection('modalEntryOrExit')
    setModalActive(true)
  }

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`,
        {
          params: {
            search: debouncedSearch,
            skip,
            take: TAKE,
            filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
          }
        },
      )
      if (response.data) {
        const newItems: Resource[] = response.data.data ?? response.data
        const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

        setResources(prev => [...prev, ...newItems])
        setTotal(totalItems)
        setSkip(prev => prev + TAKE)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={resources}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={total}
        onLoadMore={handleLoadMore}
        hasMore={resources.length < total}
        isLoading={isLoadingMore}
        countLabel={isFashionTenant ? 'aviamentos' : 'insumos'}
        exportFileName={isFashionTenant ? 'aviamentos' : 'insumos'}
        rowClassName={(resource) => (resource.isActivated === false ? 'yellowLine' : '')}
        rowActions={(resource) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => navigate(`view?id=${resource.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`edit?id=${resource.id}`) },
          ...(isFashionTenant
            ? [{ label: 'Criar Variação', icon: <Copy size={16} />, onClick: () => navigate(`create?sourceId=${resource.id}&variation=true`) }]
            : []),
          ...(resource.codeId
            ? [{ label: 'Novo movimento', icon: <ArrowLeftRight size={16} />, onClick: () => handleNewMovement(resource) }]
            : []),
          {
            label: resource.isActivated === false ? 'Ativar' : 'Inativar',
            icon: <Power size={16} />,
            onClick: () => setStatusTarget(resource),
          },
        ]}
      />

      <ConfirmationModal
        isOpen={!!statusTarget}
        title={statusTarget?.isActivated === false ? `Ativar ${itemName}` : `Inativar ${itemName}`}
        message={
          <div>
            <p>Tem certeza que deseja {statusTarget?.isActivated === false ? 'ativar' : 'inativar'} o {itemName.toLowerCase()}: &quot;{statusTarget?.name}&quot;?</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setStatusTarget(null), variant: 'outline' },
          {
            label: statusTarget?.isActivated === false ? 'Ativar' : 'Inativar',
            onClick: handleConfirmStatusChange,
            variant: statusTarget?.isActivated === false ? 'primary' : 'danger',
          },
        ]}
      />
    </>
  )
}

export default ResourcesTable
